/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/utils/hierarchy.ts
 * Release: v2.0.0 - Sistema gerarchia utenti a 4 livelli
 * Data: Dicembre 2024
 * 
 * Funzioni utility per gestire la gerarchia degli utenti nel frontend.
 * Rispecchia la logica implementata nel backend per controlli di sicurezza.
 */

export interface User {
  id: string;
  email: string;
  name?: string | null;
  surname?: string | null;
  role: string;
  group_id: string;
  status: string;
  is_verified: number;
  created_at?: string;
  group_name?: string | null;
}

/**
 * Calcola il livello gerarchico di un utente
 * @param user - Oggetto utente
 * @returns Livello numerico (1-4)
 */
export const getUserLevel = (user: User | null): number => {
  if (!user) return 0;
  
  // Livello basato su ruolo (priorità alta)
  if (user.role === 'superadmin') return 4;
  if (user.role === 'admin') return 3;
  
  // Livello basato su gruppo (supervisori)
  const groupPrivileges: Record<string, number> = {
    'accettazione': 2,  // Supervisor - gestione clienti e utenti
    'ufficio': 2,       // Supervisor - amministrazione
    'officina': 2,      // Supervisor - team tecnico
    'magazzino': 2,     // Supervisor - gestione scorte
    'client': 1,        // User base - solo auto-gestione
    'pending': 1        // User base - in attesa
  };
  
  return groupPrivileges[user.group_id] || 1; // Default: livello user
};

/**
 * Verifica se un utente può gestire un altro utente
 * @param managerUser - Utente che vuole gestire
 * @param targetUser - Utente target da gestire
 * @returns true se può gestire, false altrimenti
 */
export const canManageUser = (managerUser: User | null, targetUser: User | null): boolean => {
  if (!managerUser || !targetUser) return false;
  
  const managerLevel = getUserLevel(managerUser);
  const targetLevel = getUserLevel(targetUser);
  
  // SuperAdmin può gestire TUTTI (inclusi altri superadmin)
  if (managerLevel === 4) return true;
  
  // Altri utenti: manager deve avere livello superiore al target
  return managerLevel > targetLevel;
};

/**
 * Verifica se un utente può assegnare un determinato ruolo/gruppo
 * @param managerUser - Utente che vuole assegnare
 * @param targetRole - Ruolo da assegnare
 * @param targetGroup - Gruppo da assegnare
 * @returns true se può assegnare, false altrimenti
 */
export const canAssignLevel = (managerUser: User | null, targetRole: string, targetGroup: string): boolean => {
  if (!managerUser) return false;
  
  const managerLevel = getUserLevel(managerUser);
  const targetLevel = getUserLevel({ role: targetRole, group_id: targetGroup } as User);
  
  // SuperAdmin può assegnare QUALSIASI livello (incluso superadmin)
  if (managerLevel === 4) return true;
  
  // Altri utenti: non possono assegnare livelli uguali o superiori al proprio
  return targetLevel < managerLevel;
};

/**
 * Verifica se un utente può visualizzare un altro utente nella lista
 * @param viewerUser - Utente che sta visualizzando
 * @param targetUser - Utente target da visualizzare
 * @returns true se può visualizzare, false altrimenti
 */
export const canViewUser = (viewerUser: User | null, targetUser: User | null): boolean => {
  if (!viewerUser || !targetUser) return false;
  
  const viewerLevel = getUserLevel(viewerUser);
  const targetLevel = getUserLevel(targetUser);
  
  // SuperAdmin può vedere TUTTI
  if (viewerLevel === 4) return true;
  
  // Altri utenti: può vedere se stesso e livelli inferiori
  return viewerLevel >= targetLevel;
};

/**
 * Verifica se un utente può gestire un altro utente (incluso se stesso)
 * @param managerUser - Utente che vuole gestire
 * @param targetUser - Utente target da gestire
 * @returns true se può gestire, false altrimenti
 */
export const canManageUserIncludingSelf = (managerUser: User | null, targetUser: User | null): boolean => {
  if (!managerUser || !targetUser) return false;
  
  const managerLevel = getUserLevel(managerUser);
  const targetLevel = getUserLevel(targetUser);
  
  // SuperAdmin può gestire TUTTI (inclusi altri superadmin)
  if (managerLevel === 4) return true;
  
  // Può gestire se stesso
  if (managerUser.id === targetUser.id) return true;
  
  // Altri utenti: manager deve avere livello superiore al target
  return managerLevel > targetLevel;
};

/**
 * Filtra una lista di utenti mostrando solo quelli gestibili
 * @param users - Lista di tutti gli utenti
 * @param managerUser - Utente che sta visualizzando
 * @returns Lista filtrata di utenti gestibili
 */
export const filterManageableUsers = (users: User[], managerUser: User | null): User[] => {
  if (!managerUser) return [];
  
  return users.filter(user => canManageUser(managerUser, user));
};

/**
 * Filtra una lista di utenti mostrando solo quelli visualizzabili
 * @param users - Lista di tutti gli utenti
 * @param viewerUser - Utente che sta visualizzando
 * @returns Lista filtrata di utenti visualizzabili
 */
export const filterViewableUsers = (users: User[], viewerUser: User | null): User[] => {
  if (!viewerUser) return [];
  
  return users.filter(user => canViewUser(viewerUser, user));
};

/**
 * Ottiene descrizione testuale del livello utente
 * @param user - Utente
 * @returns Descrizione del livello
 */
export const getUserLevelDescription = (user: User | null): string => {
  if (!user) return 'Ospite';
  
  const level = getUserLevel(user);
  
  switch (level) {
    case 4: return 'SuperAdmin (Livello 4)';
    case 3: return 'Admin (Livello 3)';
    case 2: return 'Supervisor (Livello 2)';
    case 1: return 'User (Livello 1)';
    default: return 'Sconosciuto';
  }
};

/**
 * Ottiene i ruoli/gruppi assegnabili da un utente
 * @param managerUser - Utente che vuole assegnare
 * @returns Lista di opzioni assegnabili
 */
export const getAssignableOptions = (managerUser: User | null) => {
  if (!managerUser) return { roles: [], groups: [] };
  
  const managerLevel = getUserLevel(managerUser);
  
  const allRoles = [
    { value: 'superadmin', label: 'SuperAdmin', level: 4 },
    { value: 'admin', label: 'Admin', level: 3 },
    { value: 'user', label: 'User', level: 1 }
  ];
  
  const allGroups = [
    { value: 'superadmin', label: 'SuperAdmin', level: 4 },
    { value: 'admin', label: 'Admin', level: 3 },
    { value: 'accettazione', label: 'Accettazione', level: 2 },
    { value: 'ufficio', label: 'Ufficio', level: 2 },
    { value: 'officina', label: 'Officina', level: 2 },
    { value: 'magazzino', label: 'Magazzino', level: 2 },
    { value: 'client', label: 'Clienti', level: 1 },
    { value: 'pending', label: 'In Attesa', level: 1 }
  ];
  
  // SuperAdmin può assegnare tutto, altri solo livelli inferiori
  const assignableRoles = managerLevel === 4 
    ? allRoles 
    : allRoles.filter(role => role.level < managerLevel);
  const assignableGroups = managerLevel === 4 
    ? allGroups 
    : allGroups.filter(group => group.level < managerLevel);
  
  return {
    roles: assignableRoles,
    groups: assignableGroups
  };
};
